import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Stethoscope, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SPECIALIZATIONS = ["Gynecology & Obstetrics", "Cosmetic & Reconstructive Gynecology", "Infertility (IVF)", "Laparoscopic Surgery", "General Medicine"];

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", qualification: "", specialization: "Gynecology & Obstetrics", registration_no: "", clinic_name: "", clinic_address: "", clinic_phone: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await register(form);
            navigate("/");
        } catch (err) {
            setError(fmtErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-2xl animate-fade-up">
                <div className="mb-6 flex justify-center">
                    <img src="/zen-logo.webp" alt="Zen Hospital" className="h-14 w-auto" data-testid="brand-logo-img" />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="doctor-register-card">
                    <h2 className="font-heading text-2xl font-bold text-slate-900">Register your practice</h2>
                    <p className="mt-1 text-sm text-slate-500">These details appear on your prescriptions</p>
                    <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Full Name *</Label>
                            <Input data-testid="register-name-input" required value={form.name} onChange={set("name")} placeholder="Dr. Anita Sharma" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Email *</Label>
                            <Input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="doctor@clinic.com" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Password *</Label>
                            <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="Min 6 characters" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Qualification</Label>
                            <Input data-testid="register-qualification-input" value={form.qualification} onChange={set("qualification")} placeholder="MBBS, MD" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Specialization</Label>
                            <select data-testid="register-specialization-select" value={form.specialization} onChange={set("specialization")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none">
                                {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Medical Registration No.</Label>
                            <Input data-testid="register-regno-input" value={form.registration_no} onChange={set("registration_no")} placeholder="GMC-12345" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Clinic Name</Label>
                            <Input data-testid="register-clinic-name-input" value={form.clinic_name} onChange={set("clinic_name")} placeholder="City Care Clinic" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Clinic Phone</Label>
                            <Input data-testid="register-clinic-phone-input" value={form.clinic_phone} onChange={set("clinic_phone")} placeholder="+91 98XXXXXX10" />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label>Clinic Address</Label>
                            <Input data-testid="register-clinic-address-input" value={form.clinic_address} onChange={set("clinic_address")} placeholder="Shop 12, MG Road, Pune, Maharashtra" />
                        </div>
                        {error && <p data-testid="register-error" className="sm:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
                        <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="sm:col-span-2 w-full bg-[#E06D1F] hover:bg-[#B45309] text-white rounded-lg py-2.5">
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create Account
                        </Button>
                    </form>
                    <p className="mt-4 text-sm text-center text-slate-500">
                        Already registered? <Link to="/login" data-testid="login-link" className="font-medium text-[#C2410C] hover:underline">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
